<script setup>
import { useAuthStore } from '@/store/auth'
import { useConferencesStore } from '@/store/conferences'
import { defineProps, ref } from 'vue'

const conferencesStore = useConferencesStore()

const props = defineProps({
    id: Number,
    conference_seminar_id: Number,
    is_verified: Boolean,
    user_id: Number,
    user_name: String,
    user_surname: String,
    creation_time: String,
    text: String,
    user_profile_pic: String
})

const authStore = useAuthStore()

const isEdited = ref(false)
const commentText = ref(props.text)

const toggleEditComment = () => {
    isEdited.value = true
}

const toggleCancelEditing = () => {
    isEdited.value = false
    commentText.value = props.text
}

const updateComment = async () => {
    if (commentText.value.trim().length === 0) {
        return
    }

    await conferencesStore.updateComment(
        props.user_id,
        props.id,
        commentText.value
    )
    isEdited.value = false
}

const deleteComment = async () => {
    await conferencesStore.deleteComments(props.user_id, props.id)

    if (authStore.email.length > 0) {
        await conferencesStore.getComments(props.conference_seminar_id, authStore.id)
    } else {
        await conferencesStore.getComments(props.conference_seminar_id)
    }
}
</script>

<template>
    <div class="conference-comments-comment">
        <div class="conference-comments-comment-author">
            <div class="conference-comments-comment-author-data">
                <v-avatar
                    v-if="props.user_profile_pic === null"
                    icon="mdi-account"
                    color="#EFF0F2"
                    size="60"
                />
                <v-avatar
                    v-else
                    :image="document.location.origin +'/'+ props.user_profile_pic"
                    color="#EFF0F2"
                    size="60"
                />
                <h3 class="conference-comments-comment-author-name">
                    {{ `${props.user_name} ${props.user_surname}` }}
                </h3>
                <div class="conference-comments-comment-date d-none d-lg-block">
                    {{
                        `
                        ${new Date(props.creation_time).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })} 
                        в ${new Date(props.creation_time).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                    `
                    }}
                </div>
                <div class="conference-comments-comment-date d-lg-none">
                    {{
                        `
                        ${new Date(props.creation_time).toLocaleDateString('ru-RU')} 
                        в ${new Date(props.creation_time).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                    `
                    }}
                </div>
            </div>
            <v-menu
                v-if="
                    authStore.email !== 0 &&
                    authStore.id === props.user_id &&
                    !props.is_verified
                "
            >
                <template v-slot:activator="{ props }">
                    <v-icon
                        icon="mdi-dots-vertical"
                        color="#a2a2a2"
                        v-bind="props"
                    />
                </template>
                <v-list>
                    <v-list-item>
                        <v-list-item-title
                            class="menu-btn"
                            @click="toggleEditComment"
                        >
                            Изменить
                        </v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                        <v-list-item-title
                            class="menu-btn"
                            @click="deleteComment"
                        >
                            Удалить
                        </v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </div>
        <p
            v-if="!isEdited"
            class="conference-comments-comment-text"
        >
            {{ commentText }}
        </p>
        <v-text-field
            v-else
            v-model="commentText"
            variant="outlined"
            placeholder="Ваш комментарий"
            hide-details
            append-inner-icon="mdi-arrow-right-thin"
            @keyup.esc="toggleCancelEditing"
            @keyup.enter="updateComment"
            @click:append-inner="updateComment"
        />
    </div>
</template>

<style lang="scss" scoped>
.conference-comments-comment {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.conference-comments-comment-author {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.conference-comments-comment-author-data {
    display: flex;
    align-items: center;
    gap: 25px;

    .conference-comments-comment-author-name {
        font-size: 20px;
        font-weight: 500;
        line-height: 28px;
        color: #0635ac;
    }

    .conference-comments-comment-date {
        font-size: 20px;
        font-weight: 400;
        line-height: 28px;
        color: #a2a2a2;
    }

    @media screen and (max-width: 767px) {
        .conference-comments-comment-author-name {
            font-size: 14px;
        }

        .conference-comments-comment-date {
            font-size: 14px;
            margin-left: auto;
        }
    }
}

.menu-btn {
    cursor: pointer;
}

.conference-comments-comment-text {
    font-size: 20px;
    font-weight: 400;
    line-height: 28px;
}
</style>
